import React from 'react';
import { Form, Input, Select, TimePicker, InputNumber } from 'antd';
import AppModal from '@/pages/inside/tenant/AppModal';
import { Container } from '@/components/ui/Container';
import { Button } from '@/components/ui/Button';
import WorkingDaysChipsInput from './WorkingDaysChipsInput';
import { CreateRoleModalBody, CreateRoleModalFooter } from '@/pages/inside/tenant/style';

const STATUS_OPTIONS = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
];

const SALARY_TYPE_OPTIONS = [
    { value: 'fixed', label: 'Policy 1: Fixed Monthly Salary (Standard)' },
    { value: 'sandwich', label: 'Policy 2: Sandwich Rule (Weekend/Holiday Unpaid if Absent Before & After)' },
];

const ABSENT_DEDUCTION_TYPE_OPTIONS = [
    { value: 'per_day', label: 'Per Day Working Rate (Base / Scheduled Days)' },
    { value: 'fixed_amount', label: 'Fixed Amount Per Absent Day' },
];

export const CreateScheduleModal = ({
    open,
    onCancel,
    form,
    onSubmit,
    employeeOptions,
    loading,
}) => {
    return (
        <AppModal
            size="md"
            title="Create Schedule"
            open={open}
            onCancel={onCancel}
            footer={null}
            destroyOnHidden
            width={640}
            zIndex={1100}
            styles={{ body: { paddingTop: 8 } }}
        >
            <CreateRoleModalBody>
                <Form form={form} layout="vertical">
                    <Form.Item
                        name="title"
                        label="Schedule Title"
                        rules={[{ required: true, message: 'Please enter schedule title' }]}
                    >
                        <Input placeholder="e.g. Shift A" />
                    </Form.Item>
                    <Container $display="flex" $gap="16px" style={{ width: '100%' }}>
                        <Form.Item
                            name="startTime"
                            label="Start Time"
                            rules={[{ required: true, message: 'Please select start time' }]}
                            style={{ flex: 1, minWidth: 0 }}
                        >
                            <TimePicker format="HH:mm" style={{ width: '100%' }} />
                        </Form.Item>
                        <Form.Item
                            name="endTime"
                            label="End Time"
                            rules={[{ required: true, message: 'Please select end time' }]}
                            style={{ flex: 1, minWidth: 0 }}
                        >
                            <TimePicker format="HH:mm" style={{ width: '100%' }} />
                        </Form.Item>
                    </Container>
                    <Container $display="flex" $gap="16px" style={{ width: '100%' }}>
                        <Form.Item
                            name="graceTime"
                            label="Grace Time (Minutes)"
                            rules={[{ required: true, message: 'Please enter grace time' }]}
                            style={{ flex: 1, minWidth: 0 }}
                        >
                            <InputNumber min={0} placeholder="e.g. 15" style={{ width: '100%' }} />
                        </Form.Item>
                        <Form.Item
                            name="calendarEndDay"
                            label="Day End of Calendar"
                            rules={[
                                { required: true, message: 'Please enter day end of calendar' },
                                {
                                    validator: (_, value) => {
                                        if (value < 1 || value > 30) {
                                            return Promise.reject('Must be between 1 and 30');
                                        }
                                        return Promise.resolve();
                                    },
                                },
                            ]}
                            style={{ flex: 1, minWidth: 0 }}
                        >
                            <InputNumber min={1} max={30} placeholder="e.g. 10" style={{ width: '100%' }} />
                        </Form.Item>
                    </Container>
                    <Form.Item
                        name="workingDays"
                        label="Working Days"
                        rules={[{ required: true, message: 'Please select working days' }]}
                        initialValue={['mon', 'tue', 'wed', 'thu', 'fri']}
                    >
                        <WorkingDaysChipsInput />
                    </Form.Item>
                    <Container $display="flex" $gap="16px" style={{ width: '100%' }}>
                        <Form.Item
                            name="salaryType"
                            label="Salary Policy / Type"
                            rules={[{ required: true, message: 'Please select salary policy' }]}
                            initialValue="fixed"
                            style={{ flex: 1, minWidth: 0 }}
                        >
                            <Select options={SALARY_TYPE_OPTIONS} />
                        </Form.Item>
                        <Form.Item
                            name="absentDeductionType"
                            label="Absent Deduction Policy"
                            rules={[{ required: true, message: 'Please select absent deduction policy' }]}
                            initialValue="per_day"
                            style={{ flex: 1, minWidth: 0 }}
                        >
                            <Select options={ABSENT_DEDUCTION_TYPE_OPTIONS} />
                        </Form.Item>
                    </Container>
                    <Form.Item
                        noStyle
                        shouldUpdate={(prevValues, currentValues) =>
                            prevValues.absentDeductionType !== currentValues.absentDeductionType
                        }
                    >
                        {({ getFieldValue }) =>
                            getFieldValue('absentDeductionType') === 'fixed_amount' ? (
                                <Form.Item
                                    name="fixedAbsentDeductionAmount"
                                    label="Fixed Absent Deduction Amount (per day)"
                                    rules={[{ required: true, message: 'Please enter deduction amount' }]}
                                >
                                    <InputNumber min={0} prefix="Rs. " placeholder="e.g. 2000" style={{ width: '100%' }} />
                                </Form.Item>
                            ) : null
                        }
                    </Form.Item>
                    <Form.Item
                        name="status"
                        label="Status"
                        initialValue="active"
                        rules={[{ required: true, message: 'Please select status' }]}
                    >
                        <Select placeholder="Select status" options={STATUS_OPTIONS} />
                    </Form.Item>
                    <Form.Item name="userIds" label="Assign Employees (Optional)">
                        <Select
                            mode="multiple"
                            placeholder="Select employees"
                            options={employeeOptions}
                            showSearch
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </Form.Item>
                </Form>
            </CreateRoleModalBody>
            <CreateRoleModalFooter>
                <Button $primary={false} onClick={onCancel} $padding="10px 16px" $size="14px">
                    Cancel
                </Button>
                <Button
                    $primary
                    $blackOnLight
                    onClick={onSubmit}
                    loading={loading}
                    $padding="10px 16px"
                    $size="14px"
                >
                    Create
                </Button>
            </CreateRoleModalFooter>
        </AppModal>
    );
};

export default CreateScheduleModal;
